import React, { useState, useEffect } from 'react';
import {
    getPayrollOverview,
    getPayrollInstructors,
    recordPayrollSession,
    recordPayrollPayment,
    closePayrollCycle,
    reopenPayrollCycle,
    archivePayrollCycle,
    createPayrollInstructor,
    updatePayrollInstructor,
    deletePayrollInstructor,
    getInstructorPayrollHistory
} from '../../services/api';
import {
    Card,
    StatCard,
    Badge,
    ButtonPrimary,
    ButtonSecondary,
    Modal,
    Alert,
    Spinner
} from '../../components/ComponentLibrary';
import {
    Users,
    Clock,
    DollarSign,
    CreditCard,
    Plus,
    Calendar,
    ChevronLeft,
    ChevronRight,
    Eye,
    Lock,
    Unlock,
    Download,
    History,
    RefreshCw,
    Edit,
    Trash2,
    Filter,
    Archive,
    Layers
} from 'lucide-react';
import { useAuth } from '../../services/AuthContext';
import './Admin.css';
import ConfirmModal from '../../components/ConfirmModal';


const InstructorPayroll = () => {
    const { user } = useAuth();
    const isAdmin = user?.role === 'admin';

    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('ledger'); // 'ledger', 'directory', 'sessions'
    const [instructors, setInstructors] = useState([]);
    const [payrollSummary, setPayrollSummary] = useState([]);
    const [sessionLogs, setSessionLogs] = useState([]);
    const [totals, setTotals] = useState({});
    const [currentCycle, setCurrentCycle] = useState("");
    const [cycleStatus, setCycleStatus] = useState("OPEN");
    const [error, setError] = useState(null);
    const [instructorFilter, setInstructorFilter] = useState("");

    // Confirm Modal & Alert state
    const [confirmModal, setConfirmModal] = useState({
        isOpen: false,
        title: '',
        message: '',
        onConfirm: () => { },
        type: 'danger',
        singleButton: false
    });

    const showAlert = (title, message, type = 'info') => {
        setConfirmModal({
            isOpen: true,
            title,
            message,
            type,
            singleButton: true,
            onConfirm: () => setConfirmModal(prev => ({ ...prev, isOpen: false }))
        });
    };


    // Modals
    const [isInstructorModalOpen, setIsInstructorModalOpen] = useState(false);
    const [isSessionModalOpen, setIsSessionModalOpen] = useState(false);
    const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
    const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);

    // Forms
    const [instructorForm, setInstructorForm] = useState({ fullName: '', hourlyRate: '', phone: '', specialization: '' });
    const [isEditingInstructor, setIsEditingInstructor] = useState(false);
    const [editingInstructorId, setEditingInstructorId] = useState(null);

    const [sessionForm, setSessionForm] = useState({ instructorId: '', groupName: '', hours: '', sessionDate: new Date().toISOString().split('T')[0], notes: '' });
    const [paymentForm, setPaymentForm] = useState({ instructorId: '', amount: '', paymentMethod: 'InstaPay', transactionReference: '', notes: '' });

    const [selectedInstructor, setSelectedInstructor] = useState(null);
    const [instructorHistory, setInstructorHistory] = useState([]);
    const [historyLoading, setHistoryLoading] = useState(false);

    const fetchData = async (cycle = "", instId = "") => {
        try {
            setLoading(true);
            const [instRes, payrollRes] = await Promise.all([
                getPayrollInstructors(),
                getPayrollOverview(cycle, instId)
            ]);
            setInstructors(instRes.data);
            setPayrollSummary(payrollRes.summary);
            setSessionLogs(payrollRes.sessions);
            setTotals(payrollRes.totals);
            setCurrentCycle(payrollRes.cycle);
            setCycleStatus(payrollRes.status);
            setError(null);
        } catch (err) {
            setError(err.message || "Failed to load payroll data");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData(currentCycle, instructorFilter);
    }, [instructorFilter]);

    const handleInstructorSubmit = async (e) => {
        e.preventDefault();
        try {
            if (isEditingInstructor) {
                await updatePayrollInstructor(editingInstructorId, instructorForm);
            } else {
                await createPayrollInstructor(instructorForm);
            }
            setIsInstructorModalOpen(false);
            fetchData(currentCycle, instructorFilter);
        } catch (err) {
            showAlert('Save Failed', err.message || 'Failed to save instructor profile', 'danger');
        }
    };


    const handleRecordSession = async (e) => {
        e.preventDefault();
        try {
            await recordPayrollSession(sessionForm);
            setIsSessionModalOpen(false);
            setSessionForm({ instructorId: '', groupName: '', hours: '', sessionDate: new Date().toISOString().split('T')[0], notes: '' });
            fetchData(currentCycle, instructorFilter);
        } catch (err) {
            showAlert('Operation Failed', err.message || 'Failed to record session', 'danger');
        }
    };


    const handleRecordPayment = async (e) => {
        e.preventDefault();
        try {
            await recordPayrollPayment({ ...paymentForm, payrollCycle: currentCycle });
            setIsPaymentModalOpen(false);
            fetchData(currentCycle, instructorFilter);
        } catch (err) {
            showAlert('Payment Failed', err.message || 'Failed to record payment', 'danger');
        }
    };


    const handleCloseMonth = () => {
        setConfirmModal({
            isOpen: true,
            title: 'Finalize Payroll cycle?',
            message: `Are you sure you want to finalize the ${currentCycle} cycle? This will lock all session entries and prevent further modifications for this period.`,
            type: 'warning',
            singleButton: false,
            onConfirm: async () => {
                setConfirmModal(prev => ({ ...prev, isOpen: false }));
                try {
                    setLoading(true);
                    await closePayrollCycle(currentCycle);
                    fetchData(currentCycle, instructorFilter);
                    showAlert('Success', 'Cycle finalized successfully', 'info');
                } catch (err) {
                    showAlert('Operation Failed', err.message || 'Failed to close cycle', 'danger');
                    setLoading(false);
                }
            }
        });
    };


    const handleReopenMonth = () => {
        setConfirmModal({
            isOpen: true,
            title: 'Reopen Payroll Cycle?',
            message: `Do you want to reopen the ${currentCycle} cycle for modifications?`,
            type: 'warning',
            singleButton: false,
            onConfirm: async () => {
                setConfirmModal(prev => ({ ...prev, isOpen: false }));
                try {
                    setLoading(true);
                    await reopenPayrollCycle(currentCycle);
                    fetchData(currentCycle, instructorFilter);
                } catch (err) {
                    showAlert('Operation Failed', err.message || 'Failed to reopen cycle', 'danger');
                    setLoading(false);
                }
            }
        });
    };


    const navigateCycle = (direction) => {
        const months = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
        let [month, year] = currentCycle.split(" ");
        let monthIndex = months.indexOf(month);
        year = parseInt(year);

        if (direction === 'next') {
            monthIndex++;
            if (monthIndex > 11) { monthIndex = 0; year++; }
        } else {
            monthIndex--;
            if (monthIndex < 0) { monthIndex = 11; year--; }
        }
        fetchData(`${months[monthIndex]} ${year}`, instructorFilter);
    };

    const isCycleOpen = cycleStatus === 'OPEN';

    if (loading && !payrollSummary.length && !instructors.length) {
        return <div className="admin-loading-state"><Spinner size="lg" /><p>Processing session data...</p></div>;
    }

    return (
        <div className="payroll-system">
            <header className="admin-page-header">
                <div className="header-text">
                    <h2 className="finance-main-title">👨‍🏫 Instructor Payroll</h2>
                    <p className="finance-subtitle">Session-based normalized compensation tracking</p>
                </div>
                <div className="header-actions-group">
                    <div className="admin-tabs-container mr-4">
                        <button className={`admin-tab-item ${activeTab === 'ledger' ? 'active' : ''}`} onClick={() => setActiveTab('ledger')}>Ledger</button>
                        <button className={`admin-tab-item ${activeTab === 'sessions' ? 'active' : ''}`} onClick={() => setActiveTab('sessions')}>Detailed Logs</button>
                        <button className={`admin-tab-item ${activeTab === 'directory' ? 'active' : ''}`} onClick={() => setActiveTab('directory')}>Instructors</button>
                    </div>
                    <ButtonPrimary onClick={() => setIsSessionModalOpen(true)} disabled={!isCycleOpen}><Plus size={18} className="mr-2" /> Log Session</ButtonPrimary>
                </div>
            </header>

            <div className="cycle-selector-bar mb-8">
                <button className="cycle-nav-btn" onClick={() => navigateCycle('prev')}><ChevronLeft size={20} /></button>
                <div className="current-cycle-display">
                    <Calendar size={18} className="mr-2" />
                    <span>{currentCycle}</span>
                    <Badge variant={cycleStatus === 'OPEN' ? 'success' : 'error'} size="sm" className="ml-3">{cycleStatus}</Badge>
                </div>
                <button className="cycle-nav-btn" onClick={() => navigateCycle('next')}><ChevronRight size={20} /></button>

                <div className="ml-auto flex items-center gap-3">
                    <Filter size={18} className="text-slate-400" />
                    <select className="cw-input py-1 px-3 text-sm h-10 w-48" value={instructorFilter} onChange={e => setInstructorFilter(e.target.value)}>
                        <option value="">All Instructors</option>
                        {instructors.map(i => <option key={i._id} value={i._id}>{i.fullName}</option>)}
                    </select>
                </div>
            </div>

            <div className="finance-summary-grid">
                <StatCard title="Total Staff" value={totals.instructorCount} icon="user" variant="primary" />
                <StatCard title="Total Hours" value={`${totals.totalHours} hrs`} icon="clock" variant="ocean" />
                <StatCard title="Cycle Earnings" value={`${totals.totalEarnings.toLocaleString()} EGP`} icon="DollarSign" variant="indigo" />
                <StatCard title="Total Paid" value={`${totals.totalPaid.toLocaleString()} EGP`} icon="CreditCard" variant="success" />
                <StatCard title="Remaining" value={`${totals.remaining.toLocaleString()} EGP`} icon="target" variant="error" />
            </div>

            {activeTab === 'ledger' ? (
                <Card className="finance-card mt-8" hoverable={false}>
                    <div className="card-header-flex">
                        <div className="card-title-group"><h3>Instructor Summary: {currentCycle}</h3></div>
                        <div className="flex gap-2">
                            {isCycleOpen && isAdmin && <ButtonSecondary onClick={handleCloseMonth}><Lock size={18} className="mr-2" /> Close Cycle</ButtonSecondary>}
                            {!isCycleOpen && isAdmin && <ButtonSecondary onClick={handleReopenMonth}><Unlock size={18} className="mr-2" /> Reopen Cycle</ButtonSecondary>}
                        </div>
                    </div>
                    <div className="finance-table-wrapper">
                        <table className="finance-data-table">
                            <thead>
                                <tr>
                                    <th>Instructor</th>
                                    <th>Hours</th>
                                    <th>Earnings</th>
                                    <th>Paid</th>
                                    <th>Balance</th>
                                    <th>Status</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {payrollSummary.map(row => (
                                    <tr key={row.instructorId}>
                                        <td className="font-bold">{row.fullName}</td>
                                        <td>{row.totalHours}</td>
                                        <td>{row.totalEarnings.toLocaleString()} EGP</td>
                                        <td className="text-success">{row.totalPaid.toLocaleString()} EGP</td>
                                        <td className={row.remaining > 0 ? "text-danger" : "text-success font-bold"}>{row.remaining.toLocaleString()} EGP</td>
                                        <td><Badge variant={row.status === 'PAID' ? 'success' : row.status === 'PARTIAL' ? 'warning' : 'error'} size="sm">{row.status}</Badge></td>
                                        <td>
                                            <ButtonSecondary size="sm" onClick={() => {
                                                setSelectedInstructor(row);
                                                setPaymentForm({ ...paymentForm, instructorId: row.instructorId, amount: row.remaining });
                                                setIsPaymentModalOpen(true);
                                            }} disabled={row.remaining <= 0 || !isCycleOpen}><CreditCard size={14} /></ButtonSecondary>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </Card>
            ) : activeTab === 'sessions' ? (
                <Card className="finance-card mt-8" hoverable={false}>
                    <div className="card-header-flex"><h3>Detailed Session Logs: {currentCycle}</h3></div>
                    <div className="finance-table-wrapper">
                        <table className="finance-data-table">
                            <thead>
                                <tr>
                                    <th>Date</th>
                                    <th>Instructor</th>
                                    <th>Group</th>
                                    <th>Hours</th>
                                    <th>Rate</th>
                                    <th>Total</th>
                                    <th>Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {sessionLogs.map(log => (
                                    <tr key={log._id}>
                                        <td>{new Date(log.date).toLocaleDateString()}</td>
                                        <td>{log.instructor}</td>
                                        <td>{log.group}</td>
                                        <td>{log.hours}</td>
                                        <td>{log.rate} EGP</td>
                                        <td className="font-bold">{log.total.toLocaleString()} EGP</td>
                                        <td><Badge variant={log.status === 'APPROVED' ? 'indigo' : 'success'} size="sm">{log.status}</Badge></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </Card>
            ) : (
                <Card className="finance-card mt-8" hoverable={false}>
                    <div className="card-header-flex"><h3>Staff Directory</h3><ButtonPrimary onClick={() => setIsInstructorModalOpen(true)}><Plus size={18} className="mr-2" /> New Profile</ButtonPrimary></div>
                    <div className="finance-table-wrapper">
                        <table className="finance-data-table">
                            <thead>
                                <tr>
                                    <th>Name</th>
                                    <th>Base Rate</th>
                                    <th>Phone</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {instructors.map(inst => (
                                    <tr key={inst._id}>
                                        <td className="font-bold">{inst.fullName}</td>
                                        <td>{inst.hourlyRate} EGP/hr</td>
                                        <td>{inst.phone || inst.contactNumber}</td>
                                        <td><button className="table-action-btn edit" onClick={() => { setInstructorForm(inst); setIsEditingInstructor(true); setEditingInstructorId(inst._id); setIsInstructorModalOpen(true); }}><Edit size={16} /></button></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </Card>
            )}

            {/* Modal: Log Session */}
            <Modal isOpen={isSessionModalOpen} onClose={() => setIsSessionModalOpen(false)} title="⏱️ Log New Session">
                <form onSubmit={handleRecordSession} className="finance-modal-form">
                    <div className="form-grid">
                        <div className="full-width">
                            <label className="cw-input-label">Instructor</label>
                            <select className="cw-input" required value={sessionForm.instructorId} onChange={e => setSessionForm({ ...sessionForm, instructorId: e.target.value })}>
                                <option value="">Select...</option>
                                {instructors.map(i => <option key={i._id} value={i._id}>{i.fullName} ({i.hourlyRate} EGP/hr)</option>)}
                            </select>
                        </div>
                        <div className="full-width">
                            <label className="cw-input-label">Target Group (Optional)</label>
                            <input
                                className="cw-input"
                                placeholder="Type group or class name..."
                                value={sessionForm.groupName}
                                onChange={e => setSessionForm({ ...sessionForm, groupName: e.target.value })}
                            />
                        </div>
                        <div>
                            <label className="cw-input-label">Date</label>
                            <input className="cw-input" type="date" required value={sessionForm.sessionDate} onChange={e => setSessionForm({ ...sessionForm, sessionDate: e.target.value })} />
                        </div>
                        <div>
                            <label className="cw-input-label">Hours</label>
                            <input className="cw-input" type="number" step="0.5" required value={sessionForm.hours} onChange={e => setSessionForm({ ...sessionForm, hours: e.target.value })} />
                        </div>
                    </div>
                    <div className="modal-footer-actions"><ButtonPrimary type="submit" className="full-width">Submit Session</ButtonPrimary></div>
                </form>
            </Modal>

            {/* Payment Modal */}
            <Modal isOpen={isPaymentModalOpen} onClose={() => setIsPaymentModalOpen(false)} title={`💸 Disburse: ${selectedInstructor?.fullName}`}>
                <form onSubmit={handleRecordPayment} className="finance-modal-form">
                    <div className="form-grid">
                        <div className="full-width"><p>Outstanding Liability: <strong>{selectedInstructor?.remaining.toLocaleString()} EGP</strong></p></div>
                        <div><label className="cw-input-label">Amount</label><input className="cw-input" type="number" required value={paymentForm.amount} onChange={e => setPaymentForm({ ...paymentForm, amount: e.target.value })} /></div>
                        <div><label className="cw-input-label">Method</label><select className="cw-input" required value={paymentForm.paymentMethod} onChange={e => setPaymentForm({ ...paymentForm, paymentMethod: e.target.value })}>
                            <option value="InstaPay">InstaPay</option><option value="VodafoneCash">Vodafone Cash</option><option value="Cash">Cash</option>
                        </select></div>
                    </div>
                    <div className="modal-footer-actions"><ButtonPrimary type="submit" className="full-width">Confirm Disbursement</ButtonPrimary></div>
                </form>
            </Modal>

            <ConfirmModal
                isOpen={confirmModal.isOpen}
                title={confirmModal.title}
                message={confirmModal.message}
                type={confirmModal.type}
                confirmText={confirmModal.singleButton ? "Ok" : "Confirm"}
                cancelText={confirmModal.singleButton ? null : "Cancel"}
                onConfirm={confirmModal.onConfirm}
                onCancel={() => setConfirmModal(prev => ({ ...prev, isOpen: false }))}
            />
        </div>

    );
};

export default InstructorPayroll;
